import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { api } from '../../api/client.js';
import { outbox } from '../../offline/index.js';
import { flushTicketFiles, pendingTicketFiles, queueTicketFiles, subscribeTicketFiles } from './files.js';

// Tickets on this device. The last copy from the server is kept (so My
// tickets and a ticket open with no signal), plus what was sent from here
// and isn't on the server yet: a new ticket shows at once, a reply shows as
// "Sending…". Those go once the server has them.
const LOCAL_KEY = 'nu.helpdesk.local';
const MINE_KEY = 'nu.helpdesk.mine';
const ticketKey = (id) => `nu.helpdesk.t.${id}`;

const read = (k, fallback) => {
  try { return JSON.parse(localStorage.getItem(k)) ?? fallback; } catch { return fallback; }
};
const write = (k, v) => {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* lasts this visit */ }
};

// { tickets: [ticket], messages: { [ticketId]: [message] } }
let local = read(LOCAL_KEY, { tickets: [], messages: {} });
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const saveLocal = (next) => { local = next; write(LOCAL_KEY, local); emit(); };
const useLocal = () => useSyncExternalStore(subscribe, () => local);

// Once the server lists them, this device's copies can go.
function forgetSent(serverTickets = [], ticketId = null, serverMessages = null) {
  const known = new Set(serverTickets.map((t) => t.id));
  let messages = local.messages;
  if (ticketId && serverMessages) {
    const have = new Set(serverMessages.map((m) => m.id));
    const left = (messages[ticketId] || []).filter((m) => !have.has(m.id));
    messages = { ...messages, [ticketId]: left };
    if (!left.length) delete messages[ticketId];
  }
  const tickets = local.tickets.filter((t) => !known.has(t.id));
  if (tickets.length !== local.tickets.length || messages !== local.messages) saveLocal({ tickets, messages });
}

// Re-check when something was just sent (outbox, files), on return to the
// tab, and every `every` ms while the page is open.
function useRefresh(load, every) {
  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, every);
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    const off = outbox.subscribe(() => { load(); flushTicketFiles(); });
    const offFiles = subscribeTicketFiles(load);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); off(); offFiles(); };
  }, [load, every]);
}

let mineState = { list: read(MINE_KEY, null), status: 'idle' };
const mineListeners = new Set();
let mineLoading = null;
export function refreshMine() {
  if (mineLoading) return mineLoading;
  mineLoading = api('/helpdesk/mine')
    .then((list) => { write(MINE_KEY, list); mineState = { list, status: 'ready' }; forgetSent(list); })
    .catch(() => { mineState = { ...mineState, status: mineState.list ? 'ready' : 'error' }; })
    .finally(() => { mineLoading = null; mineListeners.forEach((fn) => fn()); });
  return mineLoading;
}

// Tickets I raised, newest activity first, including any still on their way.
export function useMyTickets() {
  const l = useLocal();
  const s = useSyncExternalStore((fn) => { mineListeners.add(fn); return () => mineListeners.delete(fn); }, () => mineState);
  useRefresh(refreshMine, 60_000);
  const server = s.list || [];
  const known = new Set(server.map((t) => t.id));
  const list = [...l.tickets.filter((t) => !known.has(t.id)), ...server];
  return { tickets: s.list || l.tickets.length ? list : null, status: s.status };
}

// One ticket: the server's (polled while open), else this device's copy.
export function useTicket(id) {
  const l = useLocal();
  const [state, setState] = useState(() => ({ ticket: read(ticketKey(id), null), status: 'loading' }));
  const [files, setFiles] = useState([]);
  useEffect(() => { setState({ ticket: read(ticketKey(id), null), status: 'loading' }); }, [id]);
  const load = useCallback(async () => {
    setFiles(await pendingTicketFiles(id).catch(() => []));
    try {
      const t = await api(`/helpdesk/tickets/${id}`);
      write(ticketKey(id), t);
      forgetSent([t], id, t.messages);
      setState({ ticket: t, status: 'ready' });
    } catch (e) {
      setState((s) => ({ ...s, status: e?.status === 404 ? 'missing' : 'offline' }));
    }
  }, [id]);
  useRefresh(load, 15_000);

  const mine = l.tickets.find((t) => t.id === id);
  const base = state.ticket || mine || null;
  if (!base) return { ticket: null, status: state.status, pendingFiles: files.length, reload: load };
  const have = new Set((base.messages || []).map((m) => m.id));
  const sending = (l.messages[id] || []).filter((m) => !have.has(m.id));
  return { ticket: { ...base, messages: [...(base.messages || []), ...sending] }, status: state.status, pendingFiles: files.length, reload: load };
}

// Raise a ticket (queued if there's no signal), with its photos.
export async function raiseTicket(me, { category, subject, body, stopping, device }, files = []) {
  const id = crypto.randomUUID();
  const messageId = crypto.randomUUID();
  const at = new Date().toISOString();
  const message = { id: messageId, authorId: me.id, authorName: me.name, side: 'raiser', internal: false, body, sentAt: at, local: true };
  saveLocal({
    ...local,
    tickets: [{ id, reference: null, category, subject, status: 'new', priority: stopping ? 'high' : 'normal', raisedById: me.id, raisedByName: me.name, raisedAt: at, lastMessageAt: at, lastSide: 'raiser', messages: [message], files: [], local: true }, ...local.tickets],
  });
  await outbox.enqueue('helpdesk.raise', { id, messageId, category, subject, body, stopping: Boolean(stopping), device, at });
  await queueTicketFiles(id, messageId, files);
  return id;
}

// Reply (or, for the helpdesk, an internal note) with photos.
export async function replyToTicket(me, ticketId, { body, internal = false }, files = []) {
  const id = crypto.randomUUID();
  const at = new Date().toISOString();
  saveLocal({ ...local, messages: { ...local.messages, [ticketId]: [...(local.messages[ticketId] || []), { id, authorId: me.id, authorName: me.name, side: 'local', internal, body, sentAt: at, local: true }] } });
  await outbox.enqueue('helpdesk.reply', { ticketId, id, body, internal, at });
  await queueTicketFiles(ticketId, id, files);
}

// Helpdesk only: status, priority, assignee ({ id, name } or null).
export function updateTicket(ticketId, patch) {
  return outbox.enqueue('helpdesk.update', { ticketId, ...patch });
}
